import { useId, useRef } from 'react';
import type { ReactNode } from 'react';

export interface OverflowMenuItem {
  label: string;
  onSelect: () => void;
  /** Boxicon name shown before the label. */
  icon?: string;
  disabled?: boolean;
  testId?: string;
}

/**
 * Spec 249 (review P1-2) — operator tools are not page content. Maintenance
 * that an operator runs now and then goes behind one round button at the end of
 * the page head, rather than wrapping onto two rows above the content.
 *
 * Built on the native `popover` attribute, as the account menu is: light
 * dismiss, Escape and focus return come from the platform. The panel is placed
 * from the button's box when it opens, right-aligned to it, and flips above
 * the button when there is no room below.
 *
 * Items keep the testids their buttons had, so the bundle gates that look for a
 * repair's control (196, 198, 199, 204, 205, 206) still find it.
 */
export function OverflowMenu({
  label,
  items,
  icon = 'bx-dots-horizontal-rounded',
  testId,
  children,
}: {
  /** The button's accessible name, e.g. "Maintenance". */
  label: string;
  items: readonly (OverflowMenuItem | false | null | undefined)[];
  icon?: string;
  testId?: string;
  /** Anything to render inside the panel after the items (e.g. a caption). */
  children?: ReactNode;
}) {
  const id = useId().replace(/:/g, '');
  const menuId = `hub-menu-${id}`;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const shown = items.filter(Boolean) as OverflowMenuItem[];
  if (shown.length === 0) return null;

  const place = (panel: HTMLElement) => {
    const b = buttonRef.current?.getBoundingClientRect();
    if (!b) return;
    const gap = 6;
    const h = panel.offsetHeight;
    const below = b.bottom + gap + h <= window.innerHeight;
    panel.style.top = `${below ? b.bottom + gap : Math.max(gap, b.top - gap - h)}px`;
    panel.style.right = `${Math.max(8, window.innerWidth - b.right)}px`;
    panel.style.left = 'auto';
  };

  const close = () => document.getElementById(menuId)?.hidePopover?.();

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="hub-btn hub-btn--round"
        popoverTarget={menuId}
        aria-label={label}
        title={label}
        data-testid={testId}
      >
        <i className={`bx ${icon}`} aria-hidden="true" />
      </button>
      <div
        id={menuId}
        popover="auto"
        role="menu"
        aria-label={label}
        className="hub-menu"
        data-testid={testId ? `${testId}-panel` : undefined}
        onToggle={(e) => {
          if ((e as unknown as ToggleEvent).newState === 'open') place(e.currentTarget);
        }}
      >
        {shown.map((item) => (
          <button
            key={item.label}
            type="button"
            role="menuitem"
            className="hub-menu__item"
            disabled={item.disabled}
            data-testid={item.testId}
            onClick={() => {
              close();
              item.onSelect();
            }}
          >
            {item.icon && <i className={`bx ${item.icon}`} aria-hidden="true" />}
            {item.label}
          </button>
        ))}
        {children}
      </div>
    </>
  );
}
